// src/components/ChargerStatusBadge.tsx
import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  Pressable,
  StyleSheet,
  Dimensions,
  Platform,
} from 'react-native';
import {
  ChargerAttributes,
  ChargerDisplayStatus,
  getChargerBadgeDisplayText,
  ChargerErrorInfo,
  getStatusColor,
  hasChargerErrorInfo,
  isStatusTappable,
  getChargerErrorInfo,
} from '../models/charger-model';
import { getChargerDisplayStatus } from '../utils/charger-display-status';
import ChargerStatusDetailModal from './ChargerStatusDetailModal';
import FaultTroubleshootingModal from './FaultTroubleshootingModal';
import { getUnavailabilitySummary } from '../components/common/utils/unavailable-times-utils';
import { isDemoMode, subscribeDemoMode } from '../demo/demo-mode';

const { width } = Dimensions.get('window');

interface ChargerStatusBadgeProps {
  charger: ChargerAttributes;
  size?: 'small' | 'medium' | 'large';
  style?: object;
}

/** Badge width by `ChargerDisplayStatus` from `getChargerDisplayStatus` (charger-model). */
const getBadgeWidth = (status: ChargerDisplayStatus, size: 'small' | 'medium' | 'large'): number => {
  const baseWidths: Record<ChargerDisplayStatus, number> = {
    'Available': 100,
    'In Use': 80,
    'Faulted': 90,
    'Charger Error': 130,
    'Unavailable': 110,
    'Charger Offline': 140,
    'Pending': 100,
    'Preparing': 100,
    'Vehicle Connected': 150,
    'Finishing': 100,
    'Reserved': 100,
    'SuspendedEV': 120,
    'SuspendedEVSE': 130,
  };

  const sizeMultiplier = size === 'small' ? 0.85 : size === 'large' ? 1.15 : 1;
  return Math.min(baseWidths[status] * sizeMultiplier, width * 0.3);
};

const ChargerStatusBadge: React.FC<ChargerStatusBadgeProps> = ({
  charger,
  size = 'medium',
  style,
}) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [troubleshootingVisible, setTroubleshootingVisible] = useState(false);
  const [demoOn, setDemoOn] = useState<boolean>(isDemoMode());

  useEffect(() => {
    const unsub = subscribeDemoMode((v) => setDemoOn(v));
    return () => {
      unsub();
    };
  }, []);

  const displayStatus = getChargerDisplayStatus(charger);
  const errorInfo = getChargerErrorInfo(charger);
  const severity = errorInfo?.severity || charger.error_severity;
  const backgroundColor = getStatusColor(displayStatus, severity);
  const isTappable = isStatusTappable(displayStatus, charger);
  const unavailHint = getUnavailabilitySummary(charger.unavailable_times ?? null);

  // Mocked host-troubleshooting flow — only surfaces in demo mode for chargers
  // currently reporting a fault. Not a production feature yet; the entry
  // point is intentionally gated behind the demo flag.
  const isFaultStatus =
    displayStatus === 'Faulted' || displayStatus === 'Charger Error';
  const showTroubleshooting = demoOn && isFaultStatus;
  // Pending badges are wrapped in a Pressable by the parent (opens the
  // "how to connect" info modal). Show the (i) affordance so it reads as tappable
  // even though the badge itself has no error-detail modal.
  const showInfoIcon = isTappable || displayStatus === 'Pending';

  const handlePress = () => {
    if (isTappable) {
      setModalVisible(true);
    }
  };

  const fontSize = size === 'small' ? 10 : size === 'large' ? 14 : 12;
  const paddingVertical = size === 'small' ? 4 : size === 'large' ? 10 : 8;
  const paddingHorizontal = size === 'small' ? 10 : size === 'large' ? 20 : 16;
  const badgeWidth = getBadgeWidth(displayStatus, size);

  const displayText = getChargerBadgeDisplayText(charger, displayStatus);

  const badgeContent = (
    <View
      style={[
        styles.badge,
        {
          backgroundColor,
          paddingVertical,
          paddingHorizontal,
          minWidth: badgeWidth,
        },
        showInfoIcon && styles.tappableBadge,
        style,
      ]}
    >
      <Text
        style={[
          styles.badgeText,
          { fontSize },
        ]}
        numberOfLines={1}
        ellipsizeMode="tail"
      >
        {displayText}
      </Text>
      {showInfoIcon && (
        <Text style={[styles.infoIcon, { fontSize: fontSize - 2 }]}>ⓘ</Text>
      )}
    </View>
  );

  return (
    <>
      <View style={styles.badgeStack}>
        {isTappable ? (
          <Pressable onPress={handlePress} accessibilityRole="button" accessibilityHint="Tap for more details">
            {badgeContent}
          </Pressable>
        ) : (
          badgeContent
        )}

        {unavailHint && displayStatus !== 'Unavailable' && (
          <View style={styles.unavailHintBadge}>
            <Text style={styles.unavailHintText}>{unavailHint}</Text>
          </View>
        )}
      </View>

      {hasChargerErrorInfo(charger) && (
        <ChargerStatusDetailModal
          visible={modalVisible}
          onClose={() => setModalVisible(false)}
          chargerName={charger.name}
          status={displayStatus}
          errorCode={errorInfo?.error_code || charger.error_code}
          errorDescription={errorInfo?.description || charger.error_description}
          errorUserMessage={errorInfo?.user_message || charger.error_user_message}
          errorSeverity={errorInfo?.severity || charger.error_severity}
          errorRecommendedAction={errorInfo?.recommended_action || charger.error_recommended_action}
          vendorId={charger.chargerState?.vendor_id || charger.vendor_id}
          vendorErrorCode={charger.chargerState?.vendor_error_code || charger.vendor_error_code}
          onStartTroubleshooting={
            showTroubleshooting
              ? () => {
                  setModalVisible(false);
                  setTroubleshootingVisible(true);
                }
              : undefined
          }
        />
      )}

      {showTroubleshooting && (
        <FaultTroubleshootingModal
          visible={troubleshootingVisible}
          onClose={() => setTroubleshootingVisible(false)}
          chargerName={charger.name}
          errorCode={errorInfo?.error_code || charger.error_code}
          errorUserMessage={errorInfo?.user_message || charger.error_user_message}
        />
      )}
    </>
  );
};

const styles = StyleSheet.create({
  badgeStack: {
    alignItems: 'center',
    gap: 4,
  },
  badge: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 23,
    gap: 4,
  },
  unavailHintBadge: {
    backgroundColor: '#92400E',
    borderRadius: 10,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  unavailHintText: {
    color: '#FCD34D',
    fontSize: 9,
    fontWeight: '600',
  },
  tappableBadge: {
    // Add subtle indication that it's tappable
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.2,
        shadowRadius: 2,
      },
      android: {
        elevation: 2,
      },
      web: {
        cursor: 'pointer',
      },
    }),
  },
  badgeText: {
    color: '#FFFFFF',
    fontWeight: '700',
    textAlign: 'center',
  },
  infoIcon: {
    color: '#FFFFFF',
    opacity: 0.9,
  },
});

export default ChargerStatusBadge;
